import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import socketService from '../services/socket';
import { getHubSpotContext } from '../utils/hubspotContext';

export const useSlackData = (userToken, isAuthenticated, currentWorkspace) => {
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState({});
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadReplies, setThreadReplies] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [starredItems, setStarredItems] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [lastReadTimestamps, setLastReadTimestamps] = useState({}); // channelId -> timestamp
  const [channelError, setChannelError] = useState(null); // Error state for channel access issues

  useEffect(() => {
    // Track if this effect is still mounted to avoid state updates after unmount
    let isMounted = true;

    const loadWorkspaceData = async () => {
      // Wait for currentWorkspace to be loaded - don't fetch with undefined workspace
      // This prevents the race condition where channels load with wrong/no workspace
      if (isAuthenticated && userToken && currentWorkspace?.teamId) {
        console.log('Loading workspace data for:', currentWorkspace.teamId);
        // Reset selected channel when workspace changes
        setSelectedChannel(null);
        setChannels([]);
        setMessages([]);
        setUsersLoaded(false);
        setCurrentUserId(null); // Reset current user ID to prevent showing wrong user name

        // Clear saved channel preference when switching workspaces
        localStorage.removeItem('selected_channel_id');

        // Fetch users first to ensure they're available when messages load
        try {
          const usersResponse = await api.fetchUsers(userToken, currentWorkspace.teamId);
          if (!isMounted) return; // Don't update state if unmounted

          const usersMap = {};
          usersResponse.data.forEach(user => {
            usersMap[user.id] = user;
          });
          console.log('👥 Users loaded:', {
            count: Object.keys(usersMap).length,
            userIds: Object.keys(usersMap).slice(0, 5), // Show first 5 user IDs
            userNames: Object.values(usersMap).slice(0, 5).map(u => u.real_name)
          });
          setUsers(usersMap);
          setUsersLoaded(true);
        } catch (error) {
          console.error('Error fetching users:', error);
          if (isMounted) {
            setUsersLoaded(true); // Still mark as loaded to allow app to proceed
          }
        }

        // Fetch channels after users are loaded
        try {
          const response = await api.fetchChannels(userToken, currentWorkspace.teamId);
          if (!isMounted) return; // Don't update state if unmounted

          console.log('📺 Channels loaded:', response.data.length);
          setChannels(response.data);
        } catch (error) {
          console.error('Error fetching channels:', error);
        }

        if (isMounted) {
          fetchCurrentUser();
        }
      }
    };

    loadWorkspaceData();

    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, userToken, currentWorkspace?.teamId]);

  // Restore selected channel from database after channels and users are loaded
  useEffect(() => {
    if (channels.length > 0 && !selectedChannel && usersLoaded) {
      const restoreChannel = async () => {
        let channelFound = false;

        try {
          // Get HubSpot context
          const hubspotContext = getHubSpotContext();
          const hubspotUserId = hubspotContext?.userId;

          if (hubspotUserId) {
            // Fetch from database
            const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
            const response = await fetch(`${backendUrl}/api/channel/get-preference`, {
              headers: {
                ...(hubspotContext?.handoffToken ? { 'x-hubspot-handoff': hubspotContext.handoffToken } : {})
              },
              credentials: 'include'
            });

            const data = await response.json();
            if (data.channelId) {
              const channel = channels.find(ch => ch.id === data.channelId);
              if (channel) {
                console.log('✅ Restored channel from database:', channel.name);
                selectChannel(channel);
                channelFound = true;
                return;
              } else {
                console.log('⚠️ Saved channel not found in channel list, clearing preference');
                // Clear invalid preference
                localStorage.removeItem('selected_channel_id');
              }
            }
          }

          // Fallback: try localStorage
          if (!channelFound) {
            const savedChannelId = localStorage.getItem('selected_channel_id');
            if (savedChannelId) {
              const channel = channels.find(ch => ch.id === savedChannelId);
              if (channel) {
                console.log('✅ Restored channel from localStorage:', channel.name);
                selectChannel(channel);
                channelFound = true;
              } else {
                console.log('⚠️ Saved channel from localStorage not found, clearing');
                localStorage.removeItem('selected_channel_id');
              }
            }
          }
        } catch (error) {
          console.error('Error restoring channel:', error);
          // Fallback to localStorage on error
          const savedChannelId = localStorage.getItem('selected_channel_id');
          if (savedChannelId) {
            const channel = channels.find(ch => ch.id === savedChannelId);
            if (channel) {
              selectChannel(channel);
            } else {
              localStorage.removeItem('selected_channel_id');
            }
          }
        }
      };

      restoreChannel();
    }
  }, [channels, selectedChannel, usersLoaded]);

  const markChannelAsRead = useCallback(async (channelId, timestamp) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
      await fetch(`${backendUrl}/api/unread/mark`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`
        },
        credentials: 'include',
        body: JSON.stringify({
          channel: channelId,
          timestamp: timestamp
        })
      });

      // Update local last_read timestamp
      setLastReadTimestamps(prev => ({
        ...prev,
        [channelId]: timestamp
      }));

      console.log('✅ Marked channel as read up to', timestamp);
    } catch (error) {
      console.error('Error marking channel as read:', error);
    }
  }, [userToken]);

  useEffect(() => {
    if (isAuthenticated && selectedChannel) {
      const handleNewMessage = (message) => {
        console.log('📩 New message received:', {
          user: message.user,
          userInMap: users[message.user]?.real_name,
          currentUserId: currentUserId,
          totalUsersInMap: Object.keys(users).length
        });
        if (message.channel === selectedChannel.id) {
          // Fetch user info if not already loaded
          if (message.user && !users[message.user]) {
            console.log('⚠️ User not in map, fetching:', message.user);
            fetchMissingUser(message.user);
          }

          // Auto-mark as read when new message arrives (assuming user is actively viewing)
          // This simulates Slack's behavior of marking messages as read when channel is open
          if (message.ts) {
            markChannelAsRead(selectedChannel.id, message.ts);
          }

          // Only add to main message list if it's not a thread reply
          // A thread reply has thread_ts !== ts
          if (!message.thread_ts || message.thread_ts === message.ts) {
            setMessages(prev => {
              // Check if we have an optimistic message for this user
              // Use a time-based approach: look for optimistic messages from the same user
              // sent within the last 5 seconds
              const messageTime = parseFloat(message.ts);
              const optimisticIndex = prev.findIndex(msg =>
                msg.isOptimistic &&
                msg.user === message.user &&
                Math.abs(parseFloat(msg.ts) - messageTime) < 5
              );

              if (optimisticIndex !== -1) {
                // Replace optimistic message with real message
                const newMessages = [...prev];
                newMessages[optimisticIndex] = { ...message, isOptimistic: false };
                return newMessages.sort((a, b) => parseFloat(a.ts) - parseFloat(b.ts));
              }

              // Check if message already exists (by timestamp)
              const exists = prev.some(msg => msg.ts === message.ts);
              if (exists) {
                // Update existing message
                return prev.map(msg => msg.ts === message.ts ? message : msg);
              } else {
                // Add new message and sort by timestamp
                return [...prev, message].sort((a, b) => parseFloat(a.ts) - parseFloat(b.ts));
              }
            });
          } else {
            // It's a thread reply, update the reply count of the parent
            setMessages(prev => prev.map(msg => {
              if (msg.ts === message.thread_ts) {
                // Simply increment the count - deduplication happens in handleThreadReply
                // Don't double count here
                return { ...msg, reply_count: (msg.reply_count || 0) + 1 };
              }
              return msg;
            }));

            // If this thread is currently open, add the reply to threadReplies
            if (selectedThread && (selectedThread.ts === message.thread_ts || selectedThread.thread_ts === message.thread_ts)) {
              setThreadReplies(prev => {
                // Check if we have an optimistic reply for this user
                // Use time-based approach instead of text matching
                const messageTime = parseFloat(message.ts);
                const optimisticIndex = prev.findIndex(reply =>
                  reply.isOptimistic &&
                  reply.user === message.user &&
                  Math.abs(parseFloat(reply.ts) - messageTime) < 5
                );

                if (optimisticIndex !== -1) {
                  // Replace optimistic reply with real reply, don't increment count again
                  const newReplies = [...prev];
                  newReplies[optimisticIndex] = { ...message, isOptimistic: false };
                  // Decrement the count since we're replacing, not adding
                  setMessages(msgs => msgs.map(msg => {
                    if (msg.ts === message.thread_ts) {
                      return { ...msg, reply_count: Math.max(0, (msg.reply_count || 0) - 1) };
                    }
                    return msg;
                  }));
                  return newReplies.sort((a, b) => parseFloat(a.ts) - parseFloat(b.ts));
                }

                // Check if reply already exists (by timestamp)
                const exists = prev.some(reply => reply.ts === message.ts);
                if (!exists) {
                  // Add new reply and sort by timestamp
                  return [...prev, message].sort((a, b) => parseFloat(a.ts) - parseFloat(b.ts));
                }
                return prev;
              });
            }
          }
        }
      };

      const handleMessageUpdated = (message) => {
        if (message.channel === selectedChannel.id) {
          setMessages(prev => prev.map(msg =>
            msg.ts === message.ts
              ? { ...msg, text: message.text, edited: message.edited }
              : msg
          ));

          // Also update in thread replies if it's a thread message
          if (message.thread_ts) {
            setThreadReplies(prev => prev.map(msg =>
              msg.ts === message.ts
                ? { ...msg, text: message.text, edited: message.edited }
                : msg
            ));
          }

          // Update selectedThread if it's the parent message being edited
          if (selectedThread && selectedThread.ts === message.ts) {
            setSelectedThread(prev => ({ ...prev, text: message.text, edited: message.edited }));
          }
        }
      };

      const handleMessageDeleted = (data) => {
        if (data.channel === selectedChannel.id) {
          // Check if this is a deletion by another user (not the current user)
          // If the current user deleted it, deleteMessage already handled everything
          setMessages(prev => {
            const messageExists = prev.some(msg => msg.ts === data.ts);
            if (!messageExists) {
              // Message was already removed by deleteMessage, just update count if needed
              if (data.thread_ts && data.thread_ts !== data.ts) {
                return prev.map(msg => {
                  if (msg.ts === data.thread_ts) {
                    // Only decrement if not already handled
                    return msg;
                  }
                  return msg;
                });
              }
              return prev;
            }
            // Message still exists, so this is a deletion by another user
            return prev.filter(msg => msg.ts !== data.ts);
          });

          // Also remove from thread replies if it was a thread message
          if (data.thread_ts && data.thread_ts !== data.ts) {
            setThreadReplies(prev => {
              const replyExists = prev.some(msg => msg.ts === data.ts);
              if (replyExists) {
                // This is a deletion by another user, update count
                setMessages(msgs => msgs.map(msg => {
                  if (msg.ts === data.thread_ts) {
                    return { ...msg, reply_count: Math.max(0, (msg.reply_count || 0) - 1) };
                  }
                  return msg;
                }));
                return prev.filter(msg => msg.ts !== data.ts);
              }
              return prev;
            });
          }
        }
      };

      const handleReactionAdded = (data) => {
        if (data.channel === selectedChannel.id) {
          const updateMessageReactions = (msg) => {
            if (msg.ts === data.ts) {
              const reactions = msg.reactions || [];
              const existingReaction = reactions.find(r => r.name === data.reaction);

              if (existingReaction) {
                // Add user to existing reaction if not already present
                const users = existingReaction.users || [];
                if (!users.includes(data.user)) {
                  return {
                    ...msg,
                    reactions: reactions.map(r =>
                      r.name === data.reaction
                        ? { ...r, users: [...users, data.user], count: users.length + 1 }
                        : r
                    )
                  };
                }
              } else {
                // Add new reaction
                return {
                  ...msg,
                  reactions: [...reactions, { name: data.reaction, users: [data.user], count: 1 }]
                };
              }
            }
            return msg;
          };

          setMessages(prev => prev.map(updateMessageReactions));
          setThreadReplies(prev => prev.map(updateMessageReactions));
        }
      };

      const handleReactionRemoved = (data) => {
        if (data.channel === selectedChannel.id) {
          const updateMessageReactions = (msg) => {
            if (msg.ts === data.ts) {
              const reactions = msg.reactions || [];
              const updatedReactions = reactions.map(r => {
                if (r.name === data.reaction) {
                  const updatedUsers = (r.users || []).filter(u => u !== data.user);
                  return { ...r, users: updatedUsers, count: updatedUsers.length };
                }
                return r;
              }).filter(r => r.count > 0); // Remove reactions with no users

              return { ...msg, reactions: updatedReactions };
            }
            return msg;
          };

          setMessages(prev => prev.map(updateMessageReactions));
          setThreadReplies(prev => prev.map(updateMessageReactions));
        }
      };

      socketService.onNewMessage(handleNewMessage);
      socketService.onMessageUpdated(handleMessageUpdated);
      socketService.onMessageDeleted(handleMessageDeleted);
      socketService.onReactionAdded(handleReactionAdded);
      socketService.onReactionRemoved(handleReactionRemoved);

      return () => {
        socketService.offNewMessage();
        socketService.offMessageUpdated();
        socketService.offMessageDeleted();
        socketService.offReactionAdded();
        socketService.offReactionRemoved();
      };
    }
  }, [selectedChannel, isAuthenticated, selectedThread, markChannelAsRead]);

  const fetchChannels = async () => {
    try {
      const response = await api.fetchChannels(userToken);
      setChannels(response.data);
    } catch (error) {
      console.error('Error fetching channels:', error);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await api.fetchCurrentUser(userToken, currentWorkspace?.teamId);
      setCurrentUserId(response.data.userId);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.fetchUsers(userToken, currentWorkspace?.teamId);
      const usersMap = {};
      response.data.forEach(user => {
        usersMap[user.id] = user;
      });
      setUsers(usersMap);
      setUsersLoaded(true);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsersLoaded(true); // Still mark as loaded to allow app to proceed
    }
  };

  const fetchMissingUser = async (userId) => {
    // Check if user is already being fetched or already exists
    if (users[userId] || users[userId] === null) {
      return;
    }

    // Mark as being fetched to avoid duplicate requests
    setUsers(prev => ({ ...prev, [userId]: null }));

    try {
      const response = await api.fetchUser(userId, userToken);
      if (response.data) {
        setUsers(prev => ({ ...prev, [userId]: response.data }));
      }
    } catch (error) {
      console.error('Error fetching user:', userId, error);
      // Set a placeholder to avoid repeated failed requests
      setUsers(prev => ({
        ...prev,
        [userId]: {
          id: userId,
          real_name: 'Unknown User',
          name: 'unknown'
        }
      }));
    }
  };

  const fetchMessages = useCallback(async (channelId, workspaceId = null) => {
    setLoading(true);
    setChannelError(null); // Clear previous errors
    try {
      const response = await api.fetchMessages(channelId, userToken, workspaceId);
      // Messages should already be in chronological order from backend
      // but ensure they're sorted by timestamp just in case
      const sortedMessages = response.data.sort((a, b) =>
        parseFloat(a.ts) - parseFloat(b.ts)
      );
      setMessages(sortedMessages);

      // Fetch starred items immediately to mark bookmarked messages
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        const starredResponse = await fetch(`${backendUrl}/api/bookmarks/list?token=${encodeURIComponent(userToken)}`);
        const starredData = await starredResponse.json();

        if (starredData.success && starredData.items) {
          const starredMessageTimestamps = new Set(
            starredData.items
              .filter(item => item.type === 'message')
              .map(item => item.message?.ts)
              .filter(Boolean)
          );

          // Update messages with starred status immediately
          setMessages(prev => prev.map(msg => ({
            ...msg,
            is_starred: starredMessageTimestamps.has(msg.ts)
          })));
        }
      } catch (starredError) {
        console.error('Error fetching starred items during message load:', starredError);
      }

      // Check for any missing users and fetch them
      const missingUserIds = sortedMessages
        .map(msg => msg.user)
        .filter(userId => userId && !users[userId]);

      // Fetch missing users (deduplicate)
      const uniqueMissingUserIds = [...new Set(missingUserIds)];
      uniqueMissingUserIds.forEach(userId => {
        fetchMissingUser(userId);
      });
    } catch (error) {
      console.error('Error fetching messages:', error);

      // Check for bot_not_in_channel error
      if (error.response?.status === 403 && error.response?.data?.error === 'bot_not_in_channel') {
        setChannelError({
          type: 'bot_not_in_channel',
          message: error.response.data.message,
          instruction: error.response.data.instruction
        });
        setMessages([]);
      }
    } finally {
      setLoading(false);
    }
  }, [userToken, users]);

  const selectChannel = useCallback(async (channel) => {
    if (selectedChannel) {
      socketService.leaveChannel(selectedChannel.id);
    }
    setSelectedChannel(channel);

    // Save selected channel to localStorage (for fallback)
    localStorage.setItem('selected_channel_id', channel.id);

    // Save to database for persistence across iframe reloads
    try {
      const hubspotContext = getHubSpotContext();
      const hubspotUserId = hubspotContext?.userId;

      if (hubspotUserId) {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        await fetch(`${backendUrl}/api/channel/save-preference`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(hubspotContext?.handoffToken ? { 'x-hubspot-handoff': hubspotContext.handoffToken } : {})
          },
          credentials: 'include',
          body: JSON.stringify({
            channelId: channel.id
          })
        });
        console.log('✅ Saved channel to database:', channel.name);
      }
    } catch (error) {
      console.error('Error saving channel to database:', error);
      // Continue even if database save fails - localStorage is fallback
    }

    // Ensure bot is in the channel (handles both public and private channels)
    try {
      const joinResult = await api.joinBotToChannel(channel.id, userToken, currentWorkspace?.teamId);
      console.log('Bot joined channel or already a member:', joinResult.data?.message);
      // Clear any previous channel error since bot successfully joined
      setChannelError(null);
    } catch (error) {
      console.error('Error joining/inviting bot to channel:', error);
      // If bot couldn't join private channel, show error to user
      if (error.response?.status === 403 && error.response?.data?.error === 'bot_not_in_channel') {
        setChannelError({
          type: 'bot_not_in_channel',
          message: error.response.data.message,
          instruction: error.response.data.instruction
        });
        // Don't fetch messages if bot can't access the channel
        setMessages([]);
        setLoading(false);
        return;
      }
    }

    // Fetch last_read timestamp before fetching messages
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
      const headers = {
        'Authorization': `Bearer ${userToken}`
      };
      if (currentWorkspace?.teamId) {
        headers['x-workspace-id'] = currentWorkspace.teamId;
      }
      const infoResponse = await fetch(`${backendUrl}/api/channels/${channel.id}/info`, {
        headers,
        credentials: 'include'
      });
      const infoData = await infoResponse.json();

      if (infoData.success && infoData.last_read) {
        setLastReadTimestamps(prev => ({
          ...prev,
          [channel.id]: infoData.last_read
        }));
      }
    } catch (error) {
      console.error('Error fetching channel last_read:', error);
    }

    fetchMessages(channel.id, currentWorkspace?.teamId);

    // Join channel with HubSpot sync context if enabled
    const hubspotSyncEnabled = localStorage.getItem('hubspot_sync_enabled') === 'true';
    const hubspotContext = getHubSpotContext();
    if (hubspotSyncEnabled && hubspotContext?.portalId && hubspotContext?.contactId) {
      socketService.joinChannelWithSync(channel.id, {
        enabled: true,
        portalId: hubspotContext.portalId,
        contactId: hubspotContext.contactId,
        channelName: channel.name
      });
    } else {
      socketService.joinChannel(channel.id);
    }

    // Mark as read after a short delay (to give user time to see messages)
    setTimeout(() => {
      if (messages.length > 0) {
        const latestMessage = messages[messages.length - 1];
        markChannelAsRead(channel.id, latestMessage.ts);
      }
    }, 2000);
  }, [selectedChannel, fetchMessages, userToken, messages, markChannelAsRead, currentWorkspace]);

  const sendMessage = async (text, mentions, uploadedFiles = []) => {
    if (!selectedChannel) return;

    try {
      // Create optimistic message for both file uploads and text-only messages
      // Only create optimistic message if we have a valid currentUserId to prevent wrong user display
      if ((uploadedFiles.length > 0 || (text && text.trim())) && currentUserId) {
        // Create optimistic message for immediate display
        const optimisticMessage = {
          channel: selectedChannel.id,
          text: text || '',
          ts: `${Date.now() / 1000}`, // Temporary timestamp
          user: currentUserId,
          userInfo: users[currentUserId],
          isOptimistic: true,
          tempId: `temp-${Date.now()}`, // Temporary ID to track this message
          // Use full file data from upload response for proper image preview
          files: uploadedFiles.length > 0 ? uploadedFiles : undefined
        };

        // Add message optimistically
        setMessages(prev => [...prev, optimisticMessage].sort((a, b) => parseFloat(a.ts) - parseFloat(b.ts)));
      }

      // Get HubSpot sync preference from localStorage
      const hubspotSyncEnabled = localStorage.getItem('hubspot_sync_enabled') === 'true';
      const hubspotContext = getHubSpotContext();
      const hubspotSync = hubspotSyncEnabled && hubspotContext ? {
        enabled: true,
        portalId: hubspotContext.portalId,
        userId: hubspotContext.userId,
        contactId: hubspotContext.contactId,
        channelName: selectedChannel.name
      } : null;

      if (uploadedFiles.length > 0) {
        console.log('Files uploaded, messages will be updated via socket events');
        // The file upload with initial_comment already created the message in Slack
        // and the Slack Event API will send us the new-message event which will replace the optimistic one
      } else if (text && text.trim()) {
        // Send regular text message with HubSpot sync info
        const response = await api.sendMessage(selectedChannel.id, text, userToken, mentions, currentWorkspace?.teamId, hubspotSync);
        // The Slack Event API will send the real message, which will replace our optimistic one
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Remove optimistic message on error
      setMessages(prev => prev.filter(msg => !msg.isOptimistic));

      // Check for bot_not_in_channel error
      if (error.response?.status === 403 && error.response?.data?.error === 'bot_not_in_channel') {
        setChannelError({
          type: 'bot_not_in_channel',
          message: error.response.data.message,
          instruction: error.response.data.instruction
        });
      }
    }
  };

  const selectThread = useCallback(async (message) => {
    setSelectedThread(message);
    setThreadLoading(true);

    try {
      const threadTs = message.thread_ts || message.ts;
      socketService.joinThread(selectedChannel.id, threadTs);
      const response = await api.fetchThreadReplies(selectedChannel.id, threadTs, userToken, currentWorkspace?.teamId);
      // Filter out parent message and ensure chronological order
      const replies = response.data
        .filter(msg => msg.ts !== threadTs)
        .sort((a, b) => parseFloat(a.ts) - parseFloat(b.ts));
      setThreadReplies(replies);

      // Fetch starred items immediately to mark bookmarked thread replies
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        const starredResponse = await fetch(`${backendUrl}/api/bookmarks/list?token=${encodeURIComponent(userToken)}`);
        const starredData = await starredResponse.json();

        if (starredData.success && starredData.items) {
          const starredMessageTimestamps = new Set(
            starredData.items
              .filter(item => item.type === 'message')
              .map(item => item.message?.ts)
              .filter(Boolean)
          );

          // Update thread replies with starred status immediately
          setThreadReplies(prev => prev.map(msg => ({
            ...msg,
            is_starred: starredMessageTimestamps.has(msg.ts)
          })));

          // Also update the parent thread message
          setSelectedThread(prev => ({
            ...prev,
            is_starred: starredMessageTimestamps.has(prev.ts)
          }));
        }
      } catch (starredError) {
        console.error('Error fetching starred items during thread load:', starredError);
      }

      // Check for any missing users in thread replies and fetch them
      const missingUserIds = replies
        .map(msg => msg.user)
        .filter(userId => userId && !users[userId]);

      // Fetch missing users (deduplicate)
      const uniqueMissingUserIds = [...new Set(missingUserIds)];
      uniqueMissingUserIds.forEach(userId => {
        fetchMissingUser(userId);
      });
    } catch (error) {
      console.error('Error fetching thread replies:', error);
      setThreadReplies([]);
    } finally {
      setThreadLoading(false);
    }
  }, [selectedChannel, userToken, users]);

  const closeThread = useCallback(() => {
    if (selectedThread && selectedChannel) {
      const threadTs = selectedThread.thread_ts || selectedThread.ts;
      socketService.leaveThread(selectedChannel.id, threadTs);
    }
    setSelectedThread(null);
    setThreadReplies([]);
  }, [selectedThread, selectedChannel]);

  const sendThreadReply = async (text, uploadedFiles = []) => {
    if (!selectedChannel || !selectedThread) return;

    try {
      const threadTs = selectedThread.thread_ts || selectedThread.ts;

      // Create optimistic reply for both file uploads and text-only messages
      // Only create optimistic reply if we have a valid currentUserId to prevent wrong user display
      if ((uploadedFiles.length > 0 || (text && text.trim())) && currentUserId) {
        // Create optimistic thread reply for immediate display
        const optimisticReply = {
          channel: selectedChannel.id,
          text: text || '',
          ts: `${Date.now() / 1000}`, // Temporary timestamp
          user: currentUserId,
          userInfo: users[currentUserId],
          thread_ts: threadTs,
          isOptimistic: true,
          tempId: `temp-thread-${Date.now()}`, // Temporary ID to track this reply
          // Use full file data from upload response for proper image preview
          files: uploadedFiles.length > 0 ? uploadedFiles : undefined
        };

        // Add reply optimistically
        setThreadReplies(prev => [...prev, optimisticReply]);
      }

      if (uploadedFiles.length > 0) {
        console.log('Files uploaded to thread, messages will be updated via socket events');
        // The file upload with initial_comment already created the message in Slack
        // and the Slack Event API will send us the thread-reply event which will replace the optimistic one
      } else if (text && text.trim()) {
        // Don't update reply count here - let the socket event handle it to avoid double counting
        const response = await api.sendThreadReply(selectedChannel.id, text, threadTs, userToken, currentWorkspace?.teamId);
        // The Slack Event API will send the real message, which will replace our optimistic one
        // No need to fetch replies immediately as the socket event will update them
      }
    } catch (error) {
      console.error('Error sending thread reply:', error);
      // Remove optimistic reply on error
      setThreadReplies(prev => prev.filter(reply => !reply.isOptimistic));
      // Don't need to restore count since we're not incrementing it optimistically
    }
  };

  const editMessage = async (message, newText) => {
    if (!selectedChannel) return;
    
    try {
      await api.updateMessage(selectedChannel.id, message.ts, newText, userToken, currentWorkspace?.teamId);
      
      // Update in main messages list
      setMessages(prev => prev.map(msg => 
        msg.ts === message.ts 
          ? { ...msg, text: newText, edited: true }
          : msg
      ));
      
      // Also update in thread replies if it's a thread message
      if (message.thread_ts) {
        setThreadReplies(prev => prev.map(msg => 
          msg.ts === message.ts 
            ? { ...msg, text: newText, edited: true }
            : msg
        ));
      }
      
      // Update selectedThread if it's the parent message being edited
      if (selectedThread && selectedThread.ts === message.ts) {
        setSelectedThread(prev => ({ ...prev, text: newText, edited: true }));
      }
    } catch (error) {
      console.error('Error editing message:', error);
    }
  };

  const deleteMessage = async (message) => {
    if (!selectedChannel) return;

    try {
      await api.deleteMessage(selectedChannel.id, message.ts, userToken, currentWorkspace?.teamId);

      // Remove from main messages list (only if it's not a thread reply)
      if (!message.thread_ts || message.thread_ts === message.ts) {
        setMessages(prev => prev.filter(msg => msg.ts !== message.ts));
      }

      // Also remove from thread replies if it's a thread message
      if (message.thread_ts && message.thread_ts !== message.ts) {
        // It's a thread reply, remove from thread replies list and update count
        setThreadReplies(prev => prev.filter(msg => msg.ts !== message.ts));

        // Update the parent message's reply count only once
        setMessages(prev => prev.map(msg => {
          if (msg.ts === message.thread_ts) {
            const newCount = Math.max(0, (msg.reply_count || 0) - 1);
            return { ...msg, reply_count: newCount };
          }
          return msg;
        }));
      }
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const addReaction = async (message, emoji) => {
    if (!selectedChannel) return;

    try {
      await api.addReaction(selectedChannel.id, message.ts, emoji, userToken, currentWorkspace?.teamId);

      // Update the message with the new reaction
      const updateMessageReactions = (msg) => {
        if (msg.ts === message.ts) {
          const reactions = msg.reactions || [];
          const existingReaction = reactions.find(r => r.name === emoji);

          if (existingReaction) {
            // Add current user to existing reaction
            return {
              ...msg,
              reactions: reactions.map(r =>
                r.name === emoji
                  ? { ...r, users: [...(r.users || []), currentUserId], count: (r.count || 0) + 1 }
                  : r
              )
            };
          } else {
            // Add new reaction
            return {
              ...msg,
              reactions: [...reactions, { name: emoji, users: [currentUserId], count: 1 }]
            };
          }
        }
        return msg;
      };

      setMessages(prev => prev.map(updateMessageReactions));
      if (message.thread_ts) {
        setThreadReplies(prev => prev.map(updateMessageReactions));
      }
    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  };

  const removeReaction = async (message, emoji) => {
    if (!selectedChannel) return;

    try {
      await api.removeReaction(selectedChannel.id, message.ts, emoji, userToken, currentWorkspace?.teamId);

      // Update the message to remove the reaction
      const updateMessageReactions = (msg) => {
        if (msg.ts === message.ts) {
          const reactions = msg.reactions || [];
          const updatedReactions = reactions.map(r => {
            if (r.name === emoji) {
              const updatedUsers = (r.users || []).filter(u => u !== currentUserId);
              return { ...r, users: updatedUsers, count: updatedUsers.length };
            }
            return r;
          }).filter(r => r.count > 0); // Remove reactions with no users

          return { ...msg, reactions: updatedReactions };
        }
        return msg;
      };

      setMessages(prev => prev.map(updateMessageReactions));
      if (message.thread_ts) {
        setThreadReplies(prev => prev.map(updateMessageReactions));
      }
    } catch (error) {
      console.error('Error removing reaction:', error);
    }
  };

  useEffect(() => {
    if (isAuthenticated && selectedChannel && selectedThread) {
      const handleThreadReply = (reply) => {
        const threadTs = selectedThread.thread_ts || selectedThread.ts;
        if (reply.thread_ts === threadTs && reply.channel === selectedChannel.id) {
          setThreadReplies(prev => {
            // Check if we have an optimistic reply for this user
            // Use time-based approach instead of text matching
            const replyTime = parseFloat(reply.ts);
            const optimisticIndex = prev.findIndex(msg =>
              msg.isOptimistic &&
              msg.user === reply.user &&
              Math.abs(parseFloat(msg.ts) - replyTime) < 5
            );

            if (optimisticIndex !== -1) {
              // Replace optimistic reply with real reply
              const newReplies = [...prev];
              newReplies[optimisticIndex] = { ...reply, isOptimistic: false };
              return newReplies.sort((a, b) => parseFloat(a.ts) - parseFloat(b.ts));
            }

            // Check if message already exists (by timestamp)
            const exists = prev.some(msg => msg.ts === reply.ts);
            if (exists) {
              // Update existing message
              return prev.map(msg => msg.ts === reply.ts ? reply : msg);
            } else {
              // Add new message and sort by timestamp
              return [...prev, reply].sort((a, b) => parseFloat(a.ts) - parseFloat(b.ts));
            }
          });
        }
      };

      socketService.onThreadReply(handleThreadReply);

      return () => {
        socketService.offThreadReply();
      };
    }
  }, [selectedChannel, selectedThread, isAuthenticated]);

  // Poll for starred items (bookmarks) every 2 minutes
  useEffect(() => {
    if (!isAuthenticated || !userToken) return;

    const fetchStarredItems = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        const response = await fetch(`${backendUrl}/api/bookmarks/list?token=${encodeURIComponent(userToken)}`);
        const data = await response.json();

        console.log('📌 Fetched starred items:', data);

        if (data.success && data.items) {
          setStarredItems(data.items);

          // Update messages with starred status
          const starredMessageTimestamps = new Set(
            data.items
              .filter(item => item.type === 'message')
              .map(item => item.message?.ts)
              .filter(Boolean)
          );

          console.log('📌 Starred message timestamps:', Array.from(starredMessageTimestamps));

          setMessages(prev => prev.map(msg => ({
            ...msg,
            is_starred: starredMessageTimestamps.has(msg.ts)
          })));

          setThreadReplies(prev => prev.map(msg => ({
            ...msg,
            is_starred: starredMessageTimestamps.has(msg.ts)
          })));
        }
      } catch (error) {
        console.error('Error fetching starred items:', error);
      }
    };

    // Fetch immediately
    fetchStarredItems();

    // Listen for bookmark updates to refresh immediately
    const handleBookmarkUpdate = (event) => {
      fetchStarredItems();
    };
    window.addEventListener('bookmark-updated', handleBookmarkUpdate);

    // Then poll every 2 minutes (120000ms)
    const starredInterval = setInterval(fetchStarredItems, 120000);

    return () => {
      clearInterval(starredInterval);
      window.removeEventListener('bookmark-updated', handleBookmarkUpdate);
    };
  }, [isAuthenticated, userToken]);

  // Poll for reminders every 1 minute
  useEffect(() => {
    if (!isAuthenticated || !userToken) return;

    const fetchReminders = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        const response = await fetch(`${backendUrl}/api/reminders/list?token=${encodeURIComponent(userToken)}`);
        const data = await response.json();

        if (data.success && data.reminders) {
          console.log('⏰ Fetched reminders:', data.reminders);
          setReminders(data.reminders);

          // Map reminders to messages by matching text
          // Reminders contain the message text, we need to find matching messages
          const messageReminders = {};
          data.reminders.forEach(reminder => {
            // Try to match reminder text to messages
            // Reminder text might be "Reminder: <message text>" or just the message text
            const reminderText = reminder.text.replace(/^Reminder:\s*/i, '');

            console.log('⏰ Processing reminder:', {
              original: reminder.text,
              cleaned: reminderText,
              time: reminder.time,
              id: reminder.id
            });

            // Store reminder time indexed by the message text (we'll match in the message list)
            messageReminders[reminderText] = {
              time: reminder.time,
              id: reminder.id
            };
          });

          console.log('⏰ Message reminders map:', messageReminders);

          // Update messages with reminder information
          setMessages(prev => {
            const updated = prev.map(msg => {
              const hasReminder = messageReminders[msg.text];
              if (hasReminder) {
                console.log('⏰ Matched reminder to message:', {
                  messageText: msg.text,
                  reminderTime: hasReminder.time,
                  messageTs: msg.ts
                });
              }
              return {
                ...msg,
                reminder_time: hasReminder ? hasReminder.time : null,
                reminder_id: hasReminder ? hasReminder.id : null
              };
            });
            return updated;
          });

          // Also update thread replies
          setThreadReplies(prev => prev.map(msg => {
            const hasReminder = messageReminders[msg.text];
            return {
              ...msg,
              reminder_time: hasReminder ? hasReminder.time : null,
              reminder_id: hasReminder ? hasReminder.id : null
            };
          }));

          // Check for reminders that are due (within next 5 minutes)
          const now = Math.floor(Date.now() / 1000);
          const dueReminders = data.reminders.filter(reminder => {
            const reminderTime = reminder.time;
            return reminderTime && reminderTime > now && reminderTime <= now + 300; // Within 5 minutes
          });

          // Notify user of upcoming reminders
          if (dueReminders.length > 0) {
            dueReminders.forEach(reminder => {
              console.log('⏰ Upcoming reminder:', reminder.text);
            });
          }
        }
      } catch (error) {
        console.error('Error fetching reminders:', error);
      }
    };

    // Fetch immediately
    fetchReminders();

    // Listen for reminder updates to refresh immediately
    const handleReminderUpdate = (event) => {
      fetchReminders();
    };
    window.addEventListener('reminder-updated', handleReminderUpdate);

    // Then poll every 1 minute (60000ms)
    const reminderInterval = setInterval(fetchReminders, 60000);

    return () => {
      clearInterval(reminderInterval);
      window.removeEventListener('reminder-updated', handleReminderUpdate);
    };
  }, [isAuthenticated, userToken]);

  // Polling fallback DISABLED - socket events are the primary mechanism
  // This was consuming too many Slack API rate limits (polling every 10 seconds)
  // Socket events should handle all real-time message updates
  // If socket issues occur, users can manually refresh the channel

  return {
    channels,
    selectedChannel,
    messages,
    users,
    loading,
    selectChannel,
    sendMessage,
    selectedThread,
    threadReplies,
    threadLoading,
    selectThread,
    closeThread,
    sendThreadReply,
    editMessage,
    deleteMessage,
    currentUserId,
    addReaction,
    removeReaction,
    starredItems,
    reminders,
    lastReadTimestamps,
    channelError,
    clearChannelError: () => setChannelError(null)
  };
};